import update from "immutability-helper"
import { getObjectId } from "./utils"

/**
 * Pure helpers for applying action-item changes to the missions tree
 * (mission -> planItems[] -> actions[] -> actionItems[]).
 *
 * These were previously inlined in App.updateStateForActionItemUpdateBulk, where they
 * could not be tested and where two defects lived:
 *
 *  - indexes were resolved against `this.state` while mutations accumulated in a local
 *    working copy, so any splice invalidated every index computed after it;
 *  - the "move to another action" branch spliced out of, and pushed back into, the *same*
 *    action, so a task never actually changed parent in local state.
 */

/**
 * Find an action item anywhere in a mission, by id.
 *
 * Deliberately does not trust obj.missionPlanId / obj.actionId: those describe where the
 * item *was*, which is wrong precisely when an item has been moved. Scanning also keeps
 * the result correct against a working copy that is mid-batch.
 *
 * @returns {false | {missionIndex, missionPhaseIndex, missionActionIndex, missionActionItemIndex}}
 *   `false` when the mission itself is unknown. Otherwise indexes, with -1 for "not found".
 */
export const locateActionItemIn = (missions, missionId, actionItemId) => {
    const missionIndex = missions.findIndex((m) => m._id === missionId)

    if (missionIndex === -1) return false

    const planItems = missions[missionIndex].planItems || []

    for (let p = 0; p < planItems.length; p++) {
        const actions = planItems[p].actions || []

        for (let a = 0; a < actions.length; a++) {
            const actionItems = actions[a].actionItems || []
            const found = actionItems.findIndex((ai) => ai._id === actionItemId)

            if (found !== -1) {
                return {
                    missionIndex,
                    missionPhaseIndex: p,
                    missionActionIndex: a,
                    missionActionItemIndex: found,
                }
            }
        }
    }

    return { missionIndex, missionPhaseIndex: -1, missionActionIndex: -1, missionActionItemIndex: -1 }
}

/** Find an action anywhere in a mission, by id. */
export const locateActionIn = (missions, missionId, actionId) => {
    const missionIndex = missions.findIndex((m) => m._id === missionId)

    if (missionIndex === -1) return false

    const planItems = missions[missionIndex].planItems || []

    for (let p = 0; p < planItems.length; p++) {
        const actions = planItems[p].actions || []
        const found = actions.findIndex((a) => a._id === actionId)

        if (found !== -1) {
            return { missionIndex, missionPhaseIndex: p, missionActionIndex: found }
        }
    }

    return false
}

/**
 * Apply one action-item change to a missions array, returning a new array.
 *
 * Merges in place, or relocates the item when the payload names a different actionId.
 * Returns the input untouched when the change cannot be applied, so a single bad row
 * never aborts a batch.
 *
 * @param missions  working copy of app state missions
 * @param missionId id of the mission the batch belongs to
 * @param change    { obj, change | changes }
 */
export const applyActionItemChange = (missions, missionId, change) => {
    const c = change?.change || change?.changes

    if (!change?.obj || !c) return missions

    const current = locateActionItemIn(missions, missionId, change.obj._id)

    // Not in local state. Leave it that way — a refetch that raced a local delete must not
    // resurrect the deleted item.
    if (!current || current.missionActionItemIndex === -1) return missions

    const currentAction =
        missions[current.missionIndex].planItems[current.missionPhaseIndex].actions[current.missionActionIndex]

    // Where the item should end up. c.actionId wins when the payload states one,
    // otherwise it stays where it is.
    const targetActionId = c.actionId || currentAction._id

    if (targetActionId === currentAction._id) {
        return update(missions, {
            [current.missionIndex]: {
                planItems: {
                    [current.missionPhaseIndex]: {
                        actions: {
                            [current.missionActionIndex]: {
                                actionItems: {
                                    [current.missionActionItemIndex]: { $merge: c },
                                },
                            },
                        },
                    },
                },
            },
        })
    }

    const destination = locateActionIn(missions, missionId, targetActionId)

    if (!destination) return missions

    const moved = {
        ...change.obj,
        ...currentAction.actionItems[current.missionActionItemIndex],
        ...c,
    }

    // Removing an action item cannot shift action or planItem indexes, so `destination`
    // stays valid across the splice.
    const without = update(missions, {
        [current.missionIndex]: {
            planItems: {
                [current.missionPhaseIndex]: {
                    actions: {
                        [current.missionActionIndex]: {
                            actionItems: { $splice: [[current.missionActionItemIndex, 1]] },
                        },
                    },
                },
            },
        },
    })

    return update(without, {
        [destination.missionIndex]: {
            planItems: {
                [destination.missionPhaseIndex]: {
                    actions: {
                        [destination.missionActionIndex]: {
                            actionItems: { $push: [moved] },
                        },
                    },
                },
            },
        },
    })
}

/**
 * Remove action items from a mission by id, immutably.
 *
 * The delete paths previously shallow-copied `missions` and then `.splice()`d the nested
 * actionItems arrays, which mutated live state (so React saw unchanged references) and
 * resolved every index against pre-delete state (so deleting two items from the same work
 * package removed the wrong second one).
 *
 * @param ids iterable of action item ids to remove
 */
export const removeActionItemsFrom = (missions, missionId, ids) => {
    const doomed = new Set([...ids].filter(Boolean))

    if (!doomed.size) return missions

    const missionIndex = missions.findIndex((m) => m._id === missionId)

    if (missionIndex === -1) return missions

    let next = missions

    const planItems = next[missionIndex].planItems || []

    planItems.forEach((planItem, p) => {
        const actions = planItem.actions || []

        actions.forEach((action, a) => {
            const actionItems = action.actionItems || []

            if (!actionItems.some((ai) => doomed.has(ai._id))) return

            next = update(next, {
                [missionIndex]: {
                    planItems: {
                        [p]: {
                            actions: {
                                [a]: {
                                    actionItems: {
                                        $set: actionItems.filter((ai) => !doomed.has(ai._id)),
                                    },
                                },
                            },
                        },
                    },
                },
            })
        })
    })

    return next
}

/**
 * Build the bulk changes needed to strip dependencies that point at deleted action items.
 *
 * A dependency is stored on both ends of the edge, so deleting one item leaves the other
 * side dangling. `linkTo` is the far end from the owner's perspective, but we match either
 * slot — a surviving edge can never reference a deleted item in either position.
 *
 * Safe to call before or after the items are removed from the tree.
 *
 * @returns [{ obj, change: { _id, dependencies }, removedDependencyIds }] — `change` is the
 *   shape the state reducers expect; `removedDependencyIds` are the link document ids to pass
 *   to apiActionItem.removeDependency for persistence.
 */
export const getDanglingDependencyChanges = (missions, missionId, deletedIds) => {
    const doomed = new Set([...deletedIds].filter(Boolean))

    if (!doomed.size) return []

    const mission = missions.find((m) => m._id === missionId)

    if (!mission) return []

    const changes = []

    ;(mission.planItems || []).forEach((planItem) => {
        ;(planItem.actions || []).forEach((action) => {
            ;(action.actionItems || []).forEach((ai) => {
                if (doomed.has(ai._id) || !ai.dependencies?.length) return

                const isDangling = (d) => doomed.has(getObjectId(d?.linkTo)) || doomed.has(getObjectId(d?.linkFrom))

                const kept = ai.dependencies.filter((d) => !isDangling(d))

                if (kept.length === ai.dependencies.length) return

                changes.push({
                    obj: ai,
                    change: { _id: ai._id, dependencies: kept },
                    removedDependencyIds: ai.dependencies.filter(isDangling).map((d) => getObjectId(d)),
                })
            })
        })
    })

    return changes
}

/**
 * Apply one socket message of task changes (all creates, all updates or all deletes) made in
 * other tabs, in one pass. Returns the new state pieces and one missionChange entry per
 * applied change, so the App can commit the whole message at once (see utils/missionChange.js).
 *
 * Matches the App's single-task helpers, which applied these one by one, with two
 * differences: a task moved to another activity moves here too (the old lookup searched the
 * new activity and missed it), and a create for a task already in state is skipped instead
 * of adding it twice.
 *
 * @param state {{ missions: object[], showDetailsData?: object }}
 * @param kind {"create" | "update" | "delete"}
 * @param items {{ doc: object, change?: object }[]} The message, other tabs' changes only
 * @returns {{ missions, showDetailsData, entries: object[], touchedMissionIds: string[] }}
 *   touchedMissionIds: projects a task was added to or removed from (process templates
 *   resync their flow after those)
 */
export const applyActionItemSocketChanges = (state, kind, items) => {
    let missions = state.missions
    let showDetailsData = state.showDetailsData
    const entries = []
    const touched = new Set()

    ;(items || []).forEach((item) => {
        const doc = item?.doc

        if (!doc?._id) return

        const missionId = getObjectId(doc.missionId)

        if (!missions.some((m) => m._id === missionId)) return

        if (kind === "create") {
            const existing = locateActionItemIn(missions, missionId, doc._id)

            if (existing && existing.missionActionItemIndex !== -1) return

            const at = locateActionIn(missions, missionId, getObjectId(doc.actionId))

            if (!at) return

            missions = update(missions, {
                [at.missionIndex]: {
                    planItems: {
                        [at.missionPhaseIndex]: {
                            actions: {
                                [at.missionActionIndex]: { actionItems: { $push: [doc] } },
                            },
                        },
                    },
                },
            })

            touched.add(missionId)
            entries.push({ for: "actionItem", change: "create", obj: doc, missionId })
        } else if (kind === "update") {
            const change = item.change

            if (!change) return

            missions = applyActionItemChange(missions, missionId, { obj: doc, change })

            if (showDetailsData?._id === doc._id) {
                showDetailsData = { ...showDetailsData, ...change }
            }

            entries.push({
                for: "actionItem",
                from: "socket",
                change,
                obj: { ...doc, ...change },
                missionId,
                dependencyChange: Object.prototype.hasOwnProperty.call(Object(change), "dependencies"),
            })
        } else if (kind === "delete") {
            // Strip links to the deleted task off the tasks that stay, as the single delete did
            const dangling = getDanglingDependencyChanges(missions, missionId, [doc._id])

            missions = removeActionItemsFrom(missions, missionId, [doc._id])

            dangling.forEach((c) => {
                missions = applyActionItemChange(missions, missionId, c)
            })

            touched.add(missionId)
            entries.push({
                for: "actionItem",
                change: "delete",
                obj: doc,
                missionId,
                dependencyChange: dangling.length > 0,
            })
        }
    })

    return { missions, showDetailsData, entries, touchedMissionIds: [...touched] }
}
